// Telegram-specific mobile wallet utilities for Solana
export interface TelegramWalletInfo {
  isTelegram: boolean;
  isMobile: boolean;
  userAgent: string;
  startParam?: string;
}

// Standardized storage keys - single source of truth
export const WALLET_STORAGE_KEYS = {
  // Primary unified wallet state (used by useUnifiedWallet)
  UNIFIED_STATE: 'unified_wallet_state',
  
  // Legacy individual wallet addresses (for backward compatibility)
  WALLET_ADDRESS: 'connected_wallet_address',
  WALLET_CHAIN: 'connected_wallet_chain',
  
  // Session management
  CONNECTION_SUCCESS: 'wallet_connection_success',
  CONNECTION_TIMESTAMP: 'wallet_connection_timestamp',
  SESSION_ID: 'wallet_session_id',
  
  // Phantom-specific (for deeplink flows)
  PHANTOM_PUBLIC_KEY: 'phantom_public_key',
  PHANTOM_PRIVATE_KEY: 'phantom_private_key',
} as const;

// Legacy keys that should be cleaned up
const LEGACY_STORAGE_KEYS = [
  'wallet_connection_address',
  'phantom_wallet_address',
] as const;

export const detectTelegramEnvironment = (): TelegramWalletInfo => {
  const userAgent = navigator.userAgent;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isTelegram = userAgent.includes('Telegram') || !!window.Telegram?.WebApp;
  
  // Check for wallet connection in start params
  const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
  
  console.log('Detected Telegram environment:', {
    isTelegram,
    isMobile,
    userAgent,
    startParam,
    telegramWebApp: !!window.Telegram?.WebApp
  });
  
  return {
    isTelegram,
    isMobile,
    userAgent,
    startParam
  };
};

export const getTelegramWalletInstructions = (walletInfo: TelegramWalletInfo): string => {
  if (!walletInfo.isTelegram) {
    return 'This feature is designed for Telegram Mini Apps. Please access through Telegram.';
  }
  
  if (walletInfo.isMobile) {
    return 'Tap "Connect Phantom" to open Phantom app, approve the connection, and you\'ll be redirected back automatically.';
  }
  
  return 'Click "Connect Phantom" to connect your wallet. You\'ll be redirected to complete the connection.';
};

export const checkForConnectedWallet = (): { address: string; chain: string } | null => {
  try {
    // First check the unified wallet state (primary source of truth)
    const unifiedState = localStorage.getItem(WALLET_STORAGE_KEYS.UNIFIED_STATE);
    if (unifiedState) {
      try {
        const parsed = JSON.parse(unifiedState);
        const timeDiff = Date.now() - (parsed.timestamp || 0);
        
        // Only use if less than 10 minutes old
        if (timeDiff < 10 * 60 * 1000) {
          if (parsed.activeChain === 'bsc' && parsed.bscAddress) {
            console.log('Found connected BSC wallet in unified state:', { 
              address: parsed.bscAddress.slice(0, 8) + '...', 
              chain: 'bsc' 
            });
            return { address: parsed.bscAddress, chain: 'bsc' };
          } else if (parsed.activeChain === 'solana' && parsed.solanaAddress) {
            console.log('Found connected Solana wallet in unified state:', { 
              address: parsed.solanaAddress.slice(0, 8) + '...', 
              chain: 'solana' 
            });
            return { address: parsed.solanaAddress, chain: 'solana' };
          }
          
          // Fallback to any connected wallet in unified state
          if (parsed.bscAddress) {
            return { address: parsed.bscAddress, chain: 'bsc' };
          } else if (parsed.solanaAddress) {
            return { address: parsed.solanaAddress, chain: 'solana' };
          }
        }
      } catch (parseError) {
        console.warn('Failed to parse unified wallet state:', parseError);
      }
    }
    
    // Fallback to legacy storage (for backward compatibility)
    const address = localStorage.getItem(WALLET_STORAGE_KEYS.WALLET_ADDRESS);
    if (address && address.length > 20) {
      const chain = localStorage.getItem(WALLET_STORAGE_KEYS.WALLET_CHAIN) || 'solana';
      console.log('Found connected wallet in legacy storage:', { 
        address: address.slice(0, 8) + '...', 
        chain 
      });
      return { address, chain };
    }
    
    // Check sessionStorage as final fallback
    const sessionAddress = sessionStorage.getItem(WALLET_STORAGE_KEYS.WALLET_ADDRESS);
    if (sessionAddress && sessionAddress.length > 20) {
      const sessionChain = sessionStorage.getItem(WALLET_STORAGE_KEYS.WALLET_CHAIN) || 'solana';
      console.log('Found connected wallet in sessionStorage:', { 
        address: sessionAddress.slice(0, 8) + '...', 
        chain: sessionChain 
      });
      return { address: sessionAddress, chain: sessionChain };
    }
    
    console.log('No connected wallet found in any storage');
    return null;
  } catch (error) {
    console.error('Error checking for connected wallet:', error);
    return null;
  }
};

export const clearWalletConnection = (): void => {
  try {
    // Clear unified wallet state
    localStorage.removeItem(WALLET_STORAGE_KEYS.UNIFIED_STATE);
    sessionStorage.removeItem(WALLET_STORAGE_KEYS.UNIFIED_STATE);
    
    // Clear all standardized storage keys
    Object.values(WALLET_STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    
    // Clear legacy storage keys for cleanup
    LEGACY_STORAGE_KEYS.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    
    console.log('Wallet connection cleared from all storage');
  } catch (error) {
    console.error('Error clearing wallet connection:', error);
  }
};

export const markWalletConnectionSuccess = (address: string, chain: string): void => {
  try {
    const timestamp = Date.now().toString();
    const sessionId = Math.random().toString(36).substring(2);
    
    // Store in standardized keys only
    localStorage.setItem(WALLET_STORAGE_KEYS.WALLET_ADDRESS, address);
    localStorage.setItem(WALLET_STORAGE_KEYS.WALLET_CHAIN, chain);
    localStorage.setItem(WALLET_STORAGE_KEYS.CONNECTION_SUCCESS, 'true');
    localStorage.setItem(WALLET_STORAGE_KEYS.CONNECTION_TIMESTAMP, timestamp);
    localStorage.setItem(WALLET_STORAGE_KEYS.SESSION_ID, sessionId);
    
    sessionStorage.setItem(WALLET_STORAGE_KEYS.WALLET_ADDRESS, address);
    sessionStorage.setItem(WALLET_STORAGE_KEYS.WALLET_CHAIN, chain);
    sessionStorage.setItem(WALLET_STORAGE_KEYS.CONNECTION_SUCCESS, 'true');
    sessionStorage.setItem(WALLET_STORAGE_KEYS.SESSION_ID, sessionId);
    
    // Clean up any legacy keys
    LEGACY_STORAGE_KEYS.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    
    console.log('Wallet connection marked as successful:', { 
      address: address.slice(0, 8) + '...', 
      chain,
      timestamp,
      sessionId
    });
  } catch (error) {
    console.error('Error marking wallet connection success:', error);
  }
};

export const parseWalletFromStartParam = (startParam: string): string | null => {
  if (!startParam) {
    console.log('No start param provided');
    return null;
  }
  
  console.log('Parsing start param:', startParam);
  
  if (!startParam.startsWith('wallet_connected_')) {
    console.log('Start param does not start with wallet_connected_');
    return null;
  }
  
  try {
    const encodedWallet = startParam.replace('wallet_connected_', '');
    console.log('Encoded wallet part:', encodedWallet);
    
    const decodedWallet = atob(encodedWallet);
    console.log('Decoded wallet address:', decodedWallet.slice(0, 8) + '...');
    
    // Validate the decoded wallet address (Solana addresses are 32-44 chars)
    if (isValidSolanaAddress(decodedWallet)) {
      return decodedWallet;
    } else {
      console.error('Invalid wallet address format:', decodedWallet.length);
      return null;
    }
  } catch (error) {
    console.error('Failed to parse wallet from start param:', error);
    return null;
  }
};

// Check wallet connection age using standardized timestamp
export const isWalletConnectionFresh = (): boolean => {
  try {
    const timestamp = localStorage.getItem(WALLET_STORAGE_KEYS.CONNECTION_TIMESTAMP);
    if (!timestamp) return false;
    
    const connectionTime = parseInt(timestamp);
    const now = Date.now();
    const tenMinutes = 10 * 60 * 1000; // 10 minutes in milliseconds
    
    return (now - connectionTime) < tenMinutes;
  } catch (error) {
    console.error('Error checking wallet connection freshness:', error);
    return false;
  }
};

// Validate wallet address format
export const isValidSolanaAddress = (address: string): boolean => {
  try {
    // Basic Solana address validation
    if (!address || address.length < 32 || address.length > 44) {
      return false;
    }
    
    // Check if it's a valid base58 string
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
    return base58Regex.test(address);
  } catch (error) {
    console.error('Address validation error:', error);
    return false;
  }
};

// Validate BSC/EVM address format
export const isValidEvmAddress = (address: string): boolean => {
  try {
    // Basic EVM address validation (0x + 40 hex chars)
    if (!address || address.length !== 42) {
      return false;
    }
    
    if (!address.startsWith('0x')) {
      return false;
    }
    
    // Check if it's a valid hex string
    const hexRegex = /^0x[a-fA-F0-9]{40}$/;
    return hexRegex.test(address);
  } catch (error) {
    console.error('EVM address validation error:', error);
    return false;
  }
};

// Migration utility to clean up legacy storage
export const migrateLegacyWalletStorage = (): void => {
  try {
    console.log('Starting legacy wallet storage migration...');
    
    // Check if migration is needed
    const hasLegacyData = LEGACY_STORAGE_KEYS.some(key => 
      localStorage.getItem(key) || sessionStorage.getItem(key)
    );
    
    if (!hasLegacyData) {
      console.log('No legacy wallet data found, migration not needed');
      return;
    }
    
    // Extract any valid address from legacy keys
    let migratedAddress = null;
    let migratedChain = 'solana'; // default
    
    for (const legacyKey of LEGACY_STORAGE_KEYS) {
      const address = localStorage.getItem(legacyKey);
      if (address && address.length > 20) {
        if (isValidSolanaAddress(address)) {
          migratedAddress = address;
          migratedChain = 'solana';
          break;
        } else if (isValidEvmAddress(address)) {
          migratedAddress = address;
          migratedChain = 'bsc';
          break;
        }
      }
    }
    
    // If we found valid legacy data, store it in standardized format
    if (migratedAddress) {
      console.log('Migrating legacy wallet data:', {
        address: migratedAddress.slice(0, 8) + '...',
        chain: migratedChain
      });
      
      markWalletConnectionSuccess(migratedAddress, migratedChain);
    }
    
    // Clean up legacy keys
    LEGACY_STORAGE_KEYS.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    
    console.log('Legacy wallet storage migration completed');
  } catch (error) {
    console.error('Error during legacy wallet storage migration:', error);
  }
};
